export interface BucketEntry<V = unknown> {
  count: number;
  size: number;
  /** Only populated when `bucketize` is called with `collectItems: true`. */
  items?: V[];
}

export function bucketize<T extends string, V extends { size?: number | null }>(
  items: V[],
  field: string,
  order: T[],
  opts: { collectItems?: boolean } = {}
): Map<T, BucketEntry<V>> {
  const { collectItems = false } = opts;
  const m = new Map<T, BucketEntry<V>>();
  order.forEach((k) => m.set(k, { count: 0, size: 0, items: collectItems ? [] : undefined }));

  items.forEach((item) => {
    const raw = (item as Record<string, unknown>)[field];
    const k = String(raw ?? order[0]) as T;
    if (!m.has(k)) {
      m.set(k, { count: 0, size: 0, items: collectItems ? [] : undefined });
    }
    const e = m.get(k)!;
    e.count += 1;
    e.size += item.size || 0;
    if (collectItems) {
      e.items!.push(item);
    }
  });

  return m;
}

export function bucketizeCounts<T extends string>(
  counts: Partial<Record<T, number>>,
  order: T[],
  sizeWeights?: Partial<Record<T, number>>
): Map<T, BucketEntry> {
  const m = new Map<T, BucketEntry>();
  order.forEach((k) => m.set(k, { count: 0, size: 0 }));

  order.forEach((k) => {
    const count = counts[k] ?? 0;
    if (!count) return;
    m.set(k, {
      count,
      size: sizeWeights?.[k] ?? count,
    });
  });

  return m;
}
